import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { planReviewDate } from "@amber/contracts";
import { HISTORY_OPTIONS, HISTORY_OPTION_LABELS, type HistoryOption } from "@amber/profiles/contracts";
import {
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  FieldLabel,
  ReasonDialog,
  ReasonField,
  Select,
  TextArea,
  TextInput,
  formatDateTime,
  useReason,
  useToast,
  useCommandMutation,
} from "@amber/ui";
import { CADENCE_TIERS, type SourceDetail } from "../../contracts/index";
import { profileOptionsQuery, sourceCommands, sourcePlanQuery, AFTER_PLAN_CHANGE, ON_PLAN_CONFLICT } from "../queries";

export type DialogKind = "scan" | "pause" | "resume" | "quiet" | "monitor" | "holder";

export function SourceDialog({
  dialog,
  detail,
  onClose,
}: {
  dialog: DialogKind | null;
  detail: SourceDetail;
  onClose: () => void;
}) {
  const s = detail.source;
  return (
    <>
      {dialog === "scan" ? (
        <SourceReasonDialog
          title="Scan now"
          confirm="Scan now"
          success="Scan queued"
          detail={detail}
          onClose={onClose}
          run={(body, csrf) => sourceCommands.scan(s.id, body, csrf)}
        >
          Queues an immediate scan of <b>{s.title}</b> outside its cadence
          {detail.scan_preview.people.length > 0 ? ` for ${detail.scan_preview.people.join(", ")}` : ""}. Estimated
          cost: {detail.scan_preview.estimate.currency} {detail.scan_preview.estimate.amount_decimal}.{" "}
          {detail.scan_preview.note}. It counts against the same attempt and budget limits as scheduled scans.
        </SourceReasonDialog>
      ) : null}
      {dialog === "pause" ? (
        <SourceReasonDialog
          title="Pause source"
          confirm="Pause"
          success="Source paused"
          detail={detail}
          onClose={onClose}
          run={(body, csrf) => sourceCommands.pause(s.id, body, csrf)}
        >
          Stops scanning <b>{s.title}</b> for everyone until someone resumes it; there is no automatic end.{" "}
          {s.people.length > 0
            ? `Affected: ${s.people.map((p) => p.name).join(", ")}.`
            : "No person depends on it directly."}
          {s.people.length > 1 ? " The other people depending on it lose new items too." : ""} Nothing collected is
          deleted; on resume the paused time becomes a gap.
        </SourceReasonDialog>
      ) : null}
      {dialog === "resume" ? <ResumeDialog detail={detail} onClose={onClose} /> : null}
      {dialog === "quiet" ? (
        <SourceReasonDialog
          title="Mark as expected quiet"
          confirm="Mark as expected quiet"
          success="Marked as expected quiet"
          detail={detail}
          onClose={onClose}
          run={(body, csrf) => sourceCommands.markQuiet(s.id, body, csrf)}
        >
          Records an open-ended known break starting today. <b>{s.title}</b> leaves Unusually quiet and counts as
          delivering again. The break ends when a new item appears.
        </SourceReasonDialog>
      ) : null}
      {dialog === "monitor" ? <MonitorDialog detail={detail} onClose={onClose} /> : null}
      {dialog === "holder" ? <OfficeHolderDialog detail={detail} onClose={onClose} /> : null}
    </>
  );
}

function SourceReasonDialog({
  title,
  confirm,
  success,
  detail,
  onClose,
  run,
  extra,
  children,
}: {
  title: string;
  confirm: string;
  success: string;
  detail: SourceDetail;
  onClose: () => void;
  run: (body: { reason: string; base_revision: number }, csrf: string | null) => Promise<unknown>;
  extra?: ReactNode;
  children: ReactNode;
}) {
  return (
    <ReasonDialog
      title={title}
      confirm={confirm}
      success={success}
      onClose={onClose}
      run={(reason, csrf) => run({ reason, base_revision: detail.source.revision }, csrf)}
      invalidate={AFTER_PLAN_CHANGE}
      invalidateOnConflict={ON_PLAN_CONFLICT}
      extra={extra}
    >
      {children}
    </ReasonDialog>
  );
}

function ResumeDialog({ detail, onClose }: { detail: SourceDetail; onClose: () => void }) {
  const s = detail.source;
  const [catchUp, setCatchUp] = useState(true);
  return (
    <SourceReasonDialog
      title="Resume source"
      confirm="Resume"
      success="Source resumed"
      detail={detail}
      onClose={onClose}
      run={(body, csrf) => sourceCommands.resume(s.id, { ...body, catch_up: catchUp }, csrf)}
      extra={
        <label className="mt-3 flex items-start gap-2 text-ui">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={catchUp}
            onChange={(e) => {
              setCatchUp(e.target.checked);
            }}
          />
          <span>
            <b className="font-strong">Catch up on the paused interval</b>
            <small className="block text-sm text-muted">
              {catchUp
                ? "Queues a catch-up scan behind fresh monitoring, at the usual cost per new item; the gap stays open until it has read the whole interval. It doesn't claim complete coverage."
                : "Without catch-up the gap needs someone and a task is created."}
            </small>
          </span>
        </label>
      }
    >
      Scans of <b>{s.title}</b> continue at its cadence from now on. The paused interval
      {s.pause ? ` since ${formatDateTime(s.pause.at)}` : ""} becomes a gap “Not scanned while paused”.
    </SourceReasonDialog>
  );
}

function MonitorDialog({ detail, onClose }: { detail: SourceDetail; onClose: () => void }) {
  const toast = useToast();
  const plan = useQuery(sourcePlanQuery(detail.source.id));
  const current = plan.data?.plan ?? null;
  const [tier, setTier] = useState<"priority" | "standard" | "low">(
    current?.latest.sources[0]?.cadence_tier ?? detail.source.tier,
  );
  const [history, setHistory] = useState<HistoryOption>(current?.latest.history.option ?? "default");
  const [since, setSince] = useState(current?.latest.history.since ?? "");
  const [review, setReview] = useState(current?.latest.review_date ?? planReviewDate(new Date()));
  const [rationale, setRationale] = useState(current?.latest.rationale ?? "");
  const r = useReason();
  const mutation = useCommandMutation({
    run: (csrf) =>
      sourceCommands.monitor(
        detail.source.id,
        {
          base_revision: current?.latest.revision ?? null,
          cadence_tier: tier,
          history: { option: history, since: history === "since" ? since : null },
          rationale: rationale.trim(),
          review_date: review,
          reason: r.trimmed,
        },
        csrf,
      ),
    invalidate: AFTER_PLAN_CHANGE,
    invalidateOnConflict: ON_PLAN_CONFLICT,
    onSuccess: () => {
      toast.show("Shared-source plan saved · Changes pending");
      onClose();
    },
  });
  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={current ? "Edit the shared-source plan" : "Monitor this source"}
      onSubmit={() => {
        if (r.submit() && rationale.trim().length > 0) mutation.mutate();
      }}
      footer={<DialogFooter onClose={onClose} confirm="Save plan" pending={mutation.isPending} />}
    >
      <DialogText>
        <b>{detail.source.title}</b> is monitored through its own shared-source plan, without belonging to a person.
        Appearances of tracked people found in it count for them. The plan shows Changes pending until the scheduler
        applies it; then the first scan runs.
      </DialogText>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel htmlFor="monitor-tier">Cadence tier</FieldLabel>
          <Select
            id="monitor-tier"
            value={tier}
            onChange={(e) => {
              setTier(CADENCE_TIERS.find((t) => t === e.target.value) ?? "standard");
            }}
          >
            <option value="priority">Priority · every 2 h; found within 4 h</option>
            <option value="standard">Standard · every 12 h; found within 24 h</option>
            <option value="low">Low · every 24 h; found within 48 h</option>
          </Select>
        </div>
        <div>
          <FieldLabel htmlFor="monitor-history">History</FieldLabel>
          <Select
            id="monitor-history"
            value={history}
            onChange={(e) => {
              setHistory(HISTORY_OPTIONS.find((o) => o === e.target.value) ?? "default");
            }}
          >
            {HISTORY_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {HISTORY_OPTION_LABELS[o]}
                {o === "default" ? " (central window)" : ""}
              </option>
            ))}
          </Select>
        </div>
        {history === "since" ? (
          <div>
            <FieldLabel htmlFor="monitor-since">Collect since</FieldLabel>
            <TextInput
              id="monitor-since"
              type="date"
              value={since}
              onChange={(e) => {
                setSince(e.target.value);
              }}
            />
          </div>
        ) : null}
        <div>
          <FieldLabel htmlFor="monitor-review">Review date</FieldLabel>
          <TextInput
            id="monitor-review"
            type="date"
            value={review}
            onChange={(e) => {
              setReview(e.target.value);
            }}
          />
        </div>
      </div>
      <div className="mt-3">
        <FieldLabel htmlFor="monitor-rationale" required>
          Rationale
        </FieldLabel>
        <TextArea
          id="monitor-rationale"
          rows={2}
          value={rationale}
          onChange={(e) => {
            setRationale(e.target.value);
          }}
        />
      </div>
      <ReasonField value={r.reason} onChange={r.setReason} error={r.error} />
      <FailedCallout error={mutation.error} />
    </Dialog>
  );
}

function OfficeHolderDialog({ detail, onClose }: { detail: SourceDetail; onClose: () => void }) {
  const toast = useToast();
  const s = detail.source;
  const options = useQuery(profileOptionsQuery).data?.profiles ?? [];
  const current = s.people.filter((p) => p.relationship === "office");
  const [person, setPerson] = useState("");
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [evidence, setEvidence] = useState("");
  const r = useReason();
  const mutation = useCommandMutation({
    run: (csrf) =>
      sourceCommands.changeOfficeHolder(
        s.id,
        { base_revision: s.revision, profile_id: person, from, evidence: evidence.trim(), reason: r.trimmed },
        csrf,
      ),
    invalidate: AFTER_PLAN_CHANGE,
    invalidateOnConflict: ON_PLAN_CONFLICT,
    onSuccess: () => {
      toast.show("Account holder changed");
      onClose();
    },
  });
  const missing = person === "" || evidence.trim() === "";
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Change account holder"
      onSubmit={() => {
        if (r.submit() && !missing) mutation.mutate();
      }}
      footer={<DialogFooter onClose={onClose} confirm="Change holder" pending={mutation.isPending} />}
    >
      <DialogText>
        <b>{s.title}</b> is an office account, currently held by {current.map((p) => p.name).join(", ")}. Items
        published before the new holder&apos;s start keep their attribution; newer items are attributed to the new
        holder.
      </DialogText>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <span>
          <FieldLabel htmlFor="holder-person" required>
            New holder
          </FieldLabel>
          <Select
            id="holder-person"
            value={person}
            onChange={(e) => {
              setPerson(e.target.value);
            }}
          >
            <option value="">Choose a person…</option>
            {options
              .filter((o) => !current.some((c) => c.id === o.id))
              .map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
          </Select>
        </span>
        <span>
          <FieldLabel htmlFor="holder-from" required>
            Holds it from
          </FieldLabel>
          <TextInput
            id="holder-from"
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
            }}
          />
        </span>
      </div>
      <div className="mt-3">
        <FieldLabel htmlFor="holder-evidence" required>
          Evidence
        </FieldLabel>
        <TextInput
          id="holder-evidence"
          value={evidence}
          onChange={(e) => {
            setEvidence(e.target.value);
          }}
          placeholder="For example the appointment notice"
        />
      </div>
      {r.submitted && missing ? (
        <p className="m-0 mt-2 text-md text-attention">Choose the new holder and give evidence</p>
      ) : null}
      <ReasonField value={r.reason} onChange={r.setReason} error={r.error} />
      <FailedCallout error={mutation.error} />
    </Dialog>
  );
}
